import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import SEOHead from '../components/SEOHead';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQCategory {
  title: string;
  items: FAQItem[];
}

const faqCategories: FAQCategory[] = [
  {
    title: 'Getting Started',
    items: [
      {
        question: 'How do I create an employer account?',
        answer: 'Click "Post Your First Gig Free" on the employer page and select "Employer" when registering. A new employer account includes 10 free gig posts with no credit card required.',
      },
      {
        question: 'How do the 10 free gig posts work?',
        answer: 'Every new employer account gets 10 free gig posts. Post your jobs, review profile- and preference-based matches, and see the employer workflow before choosing a paid plan.',
      },
      {
        question: 'What information should I include in a gig?',
        answer: 'Include the roles needed, dates, work location, rate, and any special requirements. Complete details help workers evaluate the opportunity and help StageCrew surface relevant profiles.',
      },
    ],
  },
  {
    title: 'Matching & Worker Quality',
    items: [
      {
        question: 'How does matching work?',
        answer: 'StageCrew uses worker profile details and job preferences to surface relevant candidates. Results depend on which active profiles and preferences align with the gig, so there is no promised match time or fill rate.',
      },
      {
        question: 'What worker information can I review?',
        answer: 'Workers provide profile details such as roles, experience, certifications, preferences, equipment, and references. Employers can review that information, contact a worker directly, and verify any qualification required for the gig. Post-gig reviews add useful history over time.',
      },
      {
        question: 'How should I handle certifications?',
        answer: 'Review the certification details a worker provides on their profile, then contact the worker and verify any credential required for your gig before making a hiring decision.',
      },
      {
        question: 'What types of roles can I hire for?',
        answer: 'Employers can post common AV, stagehand, and live event roles, including audio technicians, lighting techs, gaffers, grips, stagehands, riggers, camera operators, LED techs, stage managers, production assistants, and sound engineers.',
      },
      {
        question: 'What if I need crew urgently?',
        answer: 'Post complete dates, location, rate, and role requirements, then use the employer dashboard to review candidate source and status and contact relevant workers. Availability depends on the workers whose profiles and preferences align with the gig.',
      },
    ],
  },
  {
    title: 'Pricing & Billing',
    items: [
      {
        question: 'What are the subscription plans?',
        answer: 'The listed employer tiers are Starter at $99 per month for 5 posts, Growth at $249 per month for 20 posts, and Enterprise at $499 per month for unlimited posts. Each tier shows its included workflow features on the employer pricing page.',
      },
      {
        question: 'Can I cancel anytime?',
        answer: 'Yes — all plans are month-to-month with no long-term contracts. Cancel anytime from your dashboard settings. Your account stays active until the end of your billing period.',
      },
      {
        question: 'Are there any hidden fees or agency markups?',
        answer: 'StageCrew displays the employer plan price and posting allowance on the pricing page before signup. Worker compensation is handled directly between the employer and worker at the rate they agree.',
      },
      {
        question: 'How do worker payments work?',
        answer: 'Payment terms are agreed between you and the worker directly. StageCrew connects employers with worker profiles; compensation remains between the employer and worker.',
      },
    ],
  },
  {
    title: 'Using the Platform',
    items: [
      {
        question: 'Can I contact workers before deciding?',
        answer: 'Use the contact information available in the candidate workflow to discuss gig details, confirm availability, and verify requirements. StageCrew does not currently provide a button to start a new conversation; existing conversations remain available on the Messages page.',
      },
      {
        question: 'Where can I post a gig?',
        answer: 'Add the actual work location to each gig. Worker availability varies by location and preferences, and the employer dashboard shows the candidates connected to that specific post rather than promising a fixed market count.',
      },
      {
        question: 'How do I track candidates?',
        answer: 'The employer dashboard keeps candidate source and current status visible so you can understand how each person entered the workflow and what needs attention next.',
      },
      {
        question: 'Can I leave a review after a gig?',
        answer: 'Yes. The post-gig review workflow lets employers add feedback that can help create a more useful work history for future hiring decisions.',
      },
      {
        question: 'How do I contact support?',
        answer: 'Email edwin@stagecrew.app for employer questions. For platform issues, you can also reach support@stagecrew.app.',
      },
    ],
  },
];

// Build JSON-LD FAQPage structured data for SEO
const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqCategories.flatMap((cat) =>
    cat.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    }))
  ),
};

const FAQAccordion: React.FC<{ category: FAQCategory }> = ({ category }) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{category.title}</h2>
      <div className="space-y-2">
        {category.items.map((item, index) => (
          <div key={index} className="border border-gray-200 rounded-lg bg-white">
            <button
              onClick={() => toggle(index)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-inset rounded-lg"
              aria-expanded={openIndex === index}
            >
              <span className="font-medium text-gray-900 pr-4">{item.question}</span>
              {openIndex === index ? (
                <FaChevronUp className="text-gray-400 flex-shrink-0" aria-hidden="true" />
              ) : (
                <FaChevronDown className="text-gray-400 flex-shrink-0" aria-hidden="true" />
              )}
            </button>
            {openIndex === index && (
              <div className="px-5 pb-4 text-gray-600 leading-relaxed">
                {item.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const EmployerFAQPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Employer FAQ — StageCrew | Hire AV Crew & Stagehands"
        description="Common questions about hiring AV technicians and stagehands on StageCrew. Pricing, matching, worker quality, cancellation, and more."
        canonical="https://stagecrew.app/for-employers/faq"
        keywords="hire stagehands FAQ, AV staffing questions, event crew hiring help, stagehand staffing pricing, StageCrew employer help"
        jsonLd={faqJsonLd}
      />

      {/* Header */}
      <section className="bg-gradient-to-br from-indigo-700 to-blue-600 text-white py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Employer FAQ</h1>
          <p className="text-xl text-blue-100">
            Everything you need to know about hiring crew on StageCrew.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqCategories.map((category) => (
            <FAQAccordion key={category.title} category={category} />
          ))}

          {/* Still have questions */}
          <div className="bg-white rounded-lg shadow-sm p-8 text-center mt-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Still have questions?</h2>
            <p className="text-gray-600 mb-6">
              Email Edwin directly — he'll walk you through how StageCrew fits your crew needs.
            </p>
            <a
              href="mailto:edwin@stagecrew.app?subject=StageCrew%20Employer%20Question"
              className="inline-block bg-indigo-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-indigo-700 transition"
            >
              Email: edwin@stagecrew.app
            </a>
          </div>

          {/* CTA */}
          <div className="mt-10 bg-gradient-to-r from-indigo-700 to-blue-700 rounded-xl p-10 text-center text-white">
            <h2 className="text-2xl font-bold mb-3">Ready to hire your first crew?</h2>
            <p className="text-blue-100 mb-6">
              Post your first gig free — no credit card, no commitment.
            </p>
            <Link
              to="/register?role=employer"
              className="inline-block bg-white text-blue-700 px-10 py-3 rounded-lg font-bold text-lg hover:bg-gray-100 transition shadow-lg"
            >
              Post Your First Gig Free
            </Link>
          </div>

          {/* Quick links */}
          <div className="mt-8 text-center text-sm text-gray-500 space-x-4">
            <Link to="/for-employers" className="text-indigo-600 hover:underline">Employer Home</Link>
            <Link to="/help" className="text-indigo-600 hover:underline">General Help</Link>
            <Link to="/register?role=employer" className="text-indigo-600 hover:underline">Sign Up Free</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmployerFAQPage;
